import React, { useEffect, useMemo, useContext } from 'react'
import { useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import TabsLayoutContext from 'components/layouts/TabsLayoutContextProvider'
import { purchaseOrderFields } from 'components/containers/contracts/purchase-orders/purchaseOrdersHelpers'
import { salesOrderFields } from 'components/containers/contracts/sales-orders/salesOrdersHelpers'
import { Grid, Stack } from '@mui/material'
import { useGetCustomerQuery } from 'services/customers'
import { useGetSalesOrdersQuery } from 'services/salesOrders'
import { useGetPurchaseOrdersQuery } from 'services/purchaseOrders'
import useGlobalLoader from 'utils/custom-hooks/useGlobalLoader'
import { dateFormatter } from 'utils/data-manipulation/valueFormatter'
import { calculateTotalWeight, calculateTotalValue } from 'utils/data-manipulation/common'
import DataGridWithSearch from 'components/generic/datagrid/DataGridWithSearch'
import { getColumns } from 'components/generic/datagrid/utils'
import { DECIMAL_PRECISION_PRESETS, CURRENCY_PREPENDS } from 'utils/constants'
import { setDecimalPrecision, convertUM } from 'utils/helpers'
import { uniq } from 'lodash'
import { useCustomerHeader } from '../account/utils'

const CustomerContractsContainer = () => {
  const { id: customerId } = useParams()

  const { defaultCurrency, defaultYardUm } = useSelector(state => state.settings)
  const { currencyExchangeRatesFF } = useSelector(state => state.featureFlags?.variables || {})

  const { data: customerData, isLoading: isLoadingCustomerData } = useGetCustomerQuery(
    { customerId },
    { skip: !customerId },
  )

  const { data: purchaseOrdersData, isLoading: isLoadingPurchaseOrders } = useGetPurchaseOrdersQuery(
    { customerId },
    { skip: !customerId },
  )
  const { data: salesOrdersData, isLoading: isLoadingSalesOrders } = useGetSalesOrdersQuery(
    { customerId },
    { skip: !customerId },
  )

  const isLoading = isLoadingCustomerData || isLoadingPurchaseOrders || isLoadingSalesOrders

  const isConsumer = useMemo(() => customerData?.isConsumer, [customerData])
  const comboRows = useMemo(() => {
    if (isConsumer) {
      return salesOrdersData || []
    }
    return purchaseOrdersData || []
  }, [salesOrdersData, purchaseOrdersData])

  const tabsContext = useContext(TabsLayoutContext)
  useEffect(() => tabsContext.setContainerTabsVisibility(true), [])
  useCustomerHeader(customerData)
  useGlobalLoader(isLoading)

  const transformedRows = (comboRows || [])
    .slice()
    .filter(contract => (isConsumer ? contract.customerId === customerId : contract.supplierId === customerId))
    .sort((a, b) => new Date(b?.updatedAt) - new Date(a?.updatedAt))
    .map(ticket => {
      const ticketCurrency = ticket?.currency
      const ticketPricePrepend =
        !ticketCurrency || defaultCurrency === ticketCurrency
          ? CURRENCY_PREPENDS[defaultCurrency]?.hc
          : CURRENCY_PREPENDS[ticketCurrency]?.fc
      const materials = ticket.priceSheet?.entriesObj

      let aggregateFields
      if (uniq(materials.map(m => m.weightUnits?.toUpperCase())).length > 1) {
        aggregateFields = {
          price: 'Mixed',
          um: 'Mixed',
          ordered: 'Mixed',
          fulfilled: 'Mixed',
          remain: 'Mixed',
        }
      } else {
        const totalWeight = calculateTotalWeight(materials)
        const um = (materials?.[0]?.weightUnits || defaultYardUm).toUpperCase()
        const convertedFulfilled = convertUM(ticket.fulfilledAmount, defaultYardUm, um)
        const pricePath = isConsumer ? 'sellPrice.price' : 'buyPrice.price'
        const price = calculateTotalValue(materials, { pricePath }) / totalWeight || 0

        aggregateFields = {
          price: `${currencyExchangeRatesFF ? ticketPricePrepend : '$'}${setDecimalPrecision(price)}/${um}`,
          um,
          ordered: `${setDecimalPrecision(totalWeight, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
          fulfilled: `${setDecimalPrecision(convertedFulfilled, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
          remain: `${setDecimalPrecision(totalWeight - convertedFulfilled, DECIMAL_PRECISION_PRESETS.WEIGHT)} ${um}`,
        }
      }

      return isConsumer
        ? {
            id: ticket.id,
            readableId: ticket.salesOrderTicketId,
            salesOrderNumber: ticket.salesOrderTicketId,
            customerOrderNumber: ticket.customerPONumber,
            customerName: ticket.customer.displayName,
            effectiveDate: dateFormatter(ticket.orderSalesDetails.effectiveDate),
            expirationDate: dateFormatter(ticket.orderSalesDetails.expirationDate),
            materials,
            ...aggregateFields,
            status: ticket.status,
          }
        : {
            id: ticket.id,
            readableId: ticket.purchaseOrderTicketId,
            purchaseOrderNumber: ticket.purchaseOrderTicketId,
            customerOrderNumber: ticket.customerSONumber,
            customerName: ticket.supplier.displayName,
            effectiveDate: dateFormatter(ticket.order.effectiveDate),
            expirationDate: dateFormatter(ticket.order.expirationDate),
            materials,
            ...aggregateFields,
            status: ticket.status,
          }
    })

  const columns = [
    isConsumer ? 'salesOrderNumber' : 'purchaseOrderNumber',
    'customerOrderNumber',
    'effectiveDate',
    'expirationDate',
    'materials',
    'price',
    'ordered',
    'fulfilled',
    'remain',
    'status',
  ]

  if (isLoading) return null

  return (
    <Stack>
      <Grid p={3} sx={{ height: '100%' }} container flexDirection="column">
        <DataGridWithSearch
          rows={transformedRows}
          columns={getColumns(columns, isConsumer ? salesOrderFields : purchaseOrderFields)}
          textSearchPlaceholder={`Search ${isConsumer ? 'Sales' : 'Purchase'} Orders`}
        />
      </Grid>
    </Stack>
  )
}

export default CustomerContractsContainer
